import type { TranslationAPI } from '@lumapps/translations';

import type { WidgetPlayPropertiesType } from '../types';

/**
 * Retrieves the first video ID for a given language with automatic fallback support.
 * Uses translateObject to handle language priority and fallback automatically.
 *
 * Handles multiple data formats for backward compatibility:
 * 1. Multi-language array: { video: { en: { ids: ['abc', 'def'] }, fr: { ids: ['xyz'] } } }
 * 2. Legacy single video: { video: { id: 'abc' } }
 * 3. Legacy videoId: { videoId: 'abc' }
 *
 * @param properties - Widget properties containing video data
 * @param translateObject - Translation function to handle language fallback (from useTranslate hook)
 * @param isInDesignerMode - Whether in designer/editor mode (true) or viewer mode (false)
 * @param inputLanguage - Current input language (used in designer mode for direct language access)
 * @returns First video ID from the array for the selected language, or undefined if not found
 */
export const getVideoIdForLanguage = (
    properties: WidgetPlayPropertiesType,
    translateObject: TranslationAPI['translateObject'],
    isInDesignerMode: boolean,
    inputLanguage: string,
): string | undefined => {
    if (!properties.video) {
        // Legacy videoId format
        return properties.videoId;
    }

    const { video } = properties;

    const translatedId = isInDesignerMode
        ? video[inputLanguage]?.ids
        : (translateObject(video) as { ids: string[] })?.ids;

    // Check for multi-language: { video: { en: { ids: 'abc' }, fr: { ids: 'def' } } }
    if (translatedId) {
        return translatedId[0];
    }

    // Check for legacy single video structure: { video: { id: 'abc' } }
    if (!isInDesignerMode && 'id' in video && typeof video.id === 'string') {
        return video.id;
    }

    return undefined;
};
